/**
 * WriterContractSignModal — blocking one-time signature flow for
 * Scribes.
 *
 * Feb 2026 user request: *"Add the terms and conditions for Scribes to
 * accept before clicking accept to sign contract."*
 *
 * Flow
 *   1. On mount, GET /api/writer/signature-status.
 *   2. If needs_signature=true → render this modal.
 *   3. User reads the Terms & Conditions + the Scribe Contract, ticks
 *      the "I have read and agree…" checkbox, then clicks
 *      "Accept & Sign Contract". Button is disabled until BOTH
 *      the body has been scrolled to the bottom AND the checkbox is
 *      ticked — matches the pattern of the daily reminder popup.
 *   4. POST /api/writer/sign-contract with checkbox_confirmed=true.
 *   5. On success, modal closes; the daily-reminder popup takes over
 *      on subsequent logins.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { X, FileText, ShieldCheck, CheckCircle2, ScrollText, Info, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { authFetch } from '../utils/authUtils';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Minimal markdown → JSX renderer. Matches the WriterContractPopup
// implementation so we don't drift on formatting.
const renderMarkdown = (md) => {
  if (!md) return null;
  const lines = md.split('\n');
  const out = [];
  let list = [];
  const flush = () => {
    if (list.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc pl-5 space-y-1 my-2 text-slate-200 text-sm">
          {list.map((li, i) => <li key={i}>{li}</li>)}
        </ul>,
      );
      list = [];
    }
  };
  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    if (/^\s*-\s+/.test(line) || /^\s*\*\s+/.test(line)) {
      list.push(line.replace(/^\s*[-*]\s+/, ''));
      return;
    }
    flush();
    if (line.startsWith('# ')) {
      out.push(<h2 key={i} className="text-white text-lg font-black mt-4 mb-2">{line.slice(2)}</h2>);
    } else if (line.startsWith('## ')) {
      out.push(<h3 key={i} className="text-amber-200 text-sm font-bold uppercase tracking-wide mt-3 mb-1">{line.slice(3)}</h3>);
    } else if (!line.trim()) {
      out.push(<div key={i} className="h-2" />);
    } else if (line.startsWith('---')) {
      out.push(<hr key={i} className="border-slate-700 my-3" />);
    } else {
      const parts = line.split(/(\*\*[^*]+\*\*)/g).map((chunk, idx) => {
        if (chunk.startsWith('**') && chunk.endsWith('**')) {
          return <strong key={idx} className="text-white">{chunk.slice(2, -2)}</strong>;
        }
        return <React.Fragment key={idx}>{chunk}</React.Fragment>;
      });
      out.push(<p key={i} className="text-slate-200 text-sm leading-relaxed my-1.5">{parts}</p>);
    }
  });
  flush();
  return out;
};

const DECLARATION_TEXT = "I confirm that I have read and understood the SmarterYou Writer Terms & Conditions and the Scribe (Writer) Agreement, and I agree to be bound by them.";

const WriterContractSignModal = ({ user, onSigned }) => {
  const [status, setStatus] = useState(null);      // full server response
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);

  const roleEligible = user && ['writer', 'admin', 'king', 'coadmin'].includes((user.role || '').toLowerCase());

  const fetchStatus = useCallback(async () => {
    if (!roleEligible) return;
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/writer/signature-status`);
      if (!r.ok) return;
      const data = await r.json();
      setStatus(data);
      if (data?.needs_signature) setOpen(true);
    } catch {
      /* non-critical */
    } finally {
      setLoading(false);
    }
  }, [roleEligible]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleSign = async () => {
    if (!checked) {
      toast.error("Please tick the acknowledgement checkbox first.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/writer/sign-contract`, {
        method: 'POST',
        body: JSON.stringify({
          checkbox_confirmed: true,
          declaration_text: DECLARATION_TEXT,
        }),
      });
      const data = await r.json();
      if (!r.ok || !data.signed) {
        throw new Error(data.detail || 'Failed to sign');
      }
      toast.success('✓ Contract signed — welcome aboard.');
      setOpen(false);
      if (typeof onSigned === 'function') onSigned(data);
    } catch (e) {
      toast.error(e.message || 'Signing failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !status?.terms || !status?.contract) return null;

  const canSign = checked && scrolledToBottom && !submitting;

  return (
    <div
      className="fixed inset-0 z-[220] flex items-center justify-center px-4 py-6 bg-black/80 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="writer-sign-modal-title"
      data-testid="writer-sign-modal"
    >
      <div className="w-full max-w-3xl bg-slate-900 border-2 border-amber-500/50 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-800 bg-gradient-to-r from-amber-900/40 via-orange-900/20 to-slate-900">
          <div className="flex items-start gap-3 min-w-0">
            <FileText className="w-6 h-6 text-amber-300 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <h2 id="writer-sign-modal-title" className="text-white text-base sm:text-lg font-black">
                Sign your SmarterYou Scribe Contract
              </h2>
              <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 border border-amber-400/40 uppercase tracking-widest font-bold">
                  <ShieldCheck className="w-3 h-3" /> One-time
                </span>
                <span>· T&amp;C v{status.terms.version} · Contract v{status.contract.version}</span>
              </p>
            </div>
          </div>
          {/* No dismiss button — this is a blocking modal until signed. */}
        </div>

        {/* Body — scrollable, contains T&C then Contract */}
        <div
          className="flex-1 overflow-y-auto px-5 py-4 select-text"
          data-testid="writer-sign-modal-body"
          onScroll={(e) => {
            const el = e.currentTarget;
            if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
              setScrolledToBottom(true);
            }
          }}
        >
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 text-[12px] text-amber-100 mb-4 flex items-start gap-2">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              Please read both documents in full. Scroll to the bottom to enable the acknowledgement checkbox, tick it, then click <strong>Accept &amp; Sign Contract</strong>.
            </div>
          </div>

          {/* Section 1 — Terms & Conditions */}
          <div className="mb-6" data-testid="writer-sign-modal-terms">
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold mb-2 flex items-center gap-1.5">
              <ScrollText className="w-3.5 h-3.5" /> Part 1 · Pre-Contract Terms &amp; Conditions
            </div>
            {renderMarkdown(status.terms.markdown)}
          </div>

          <hr className="border-slate-700 my-4" />

          {/* Section 2 — the Contract itself */}
          <div className="mb-4" data-testid="writer-sign-modal-contract">
            <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold mb-2 flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5" /> Part 2 · Scribe (Writer) Agreement
            </div>
            {renderMarkdown(status.contract.markdown)}
          </div>

          {/* End-of-body anchor to help humans see the checkbox area */}
          <div className="mt-6 rounded-lg border border-slate-700 bg-slate-800/50 px-3 py-3">
            <p className="text-[11px] text-slate-400 italic">
              You have reached the end of the documents. Please review the declaration below and tick the checkbox to sign.
            </p>
          </div>
        </div>

        {/* Footer — declaration + checkbox + accept */}
        <div className="px-5 py-3 border-t border-slate-800 bg-slate-950/70 space-y-3">
          <label
            className={`flex items-start gap-2.5 text-sm cursor-pointer select-none ${scrolledToBottom ? 'text-slate-200' : 'text-slate-500'}`}
            data-testid="writer-sign-modal-checkbox-label"
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              disabled={!scrolledToBottom}
              className="mt-0.5 h-4 w-4 accent-amber-500 disabled:opacity-40"
              data-testid="writer-sign-modal-checkbox"
            />
            <span>
              {DECLARATION_TEXT}
              {!scrolledToBottom && (
                <span className="block text-[10px] text-amber-300/80 mt-0.5">
                  (Scroll the documents above to the bottom to enable this checkbox.)
                </span>
              )}
            </span>
          </label>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[11px] text-slate-500">
              Signing as <strong className="text-slate-300">{user?.name || user?.email || 'you'}</strong>. Your signature is timestamped and audit-logged.
            </p>
            <Button
              onClick={handleSign}
              disabled={!canSign}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold disabled:opacity-40"
              data-testid="writer-sign-modal-accept"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-1.5" />
                  Accept &amp; Sign Contract
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WriterContractSignModal;
